/**
 * ALTIORA Radar — Sources Manager (Admin).
 *
 * Route: /app/radar/sources
 *
 * Displays every community_source with status filters, probes/counters,
 * inline actions (approve/reject/activate/deactivate/test/diagnose).
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Search, Zap, Ban, Check, PlayCircle,
          PauseCircle, AlertCircle, X, Activity, Sparkles } from "lucide-react";

const NAVY = "#0a2540";
const CHAMPAGNE = "#c9a34e";
const SLATE = "#5a6b7a";

const STATUS_STYLES = {
  discovered:   { bg: "#e0f2fe", fg: "#0369a1", label: "Découverte" },
  candidate:    { bg: "#e0f2fe", fg: "#0369a1", label: "Candidate (legacy)" },
  verifying:    { bg: "#fef3c7", fg: "#92400e", label: "Vérification" },
  approved:     { bg: "#dcfce7", fg: "#15803d", label: "Approuvée" },
  active:       { bg: "#bbf7d0", fg: "#14532d", label: "Active" },
  degraded:     { bg: "#fed7aa", fg: "#9a3412", label: "Dégradée" },
  blocked:      { bg: "#fecaca", fg: "#991b1b", label: "Bloquée" },
  rejected:     { bg: "#e5e7eb", fg: "#374151", label: "Rejetée" },
  manual_only:  { bg: "#f3e8ff", fg: "#6b21a8", label: "Manuelle" },
  inactive:     { bg: "#e5e7eb", fg: "#374151", label: "Inactive" },
};

function StatusPill({ status }) {
  const st = STATUS_STYLES[status] || STATUS_STYLES.inactive;
  return (
    <span data-testid={`status-${status}`}
          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
          style={{ background: st.bg, color: st.fg }}>{st.label}</span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      dateStyle: "short", timeStyle: "short",
    });
  } catch { return iso; }
}

export default function SourcesManager() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [scheduler, setScheduler] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, sched, k] = await Promise.all([
        api.get("/intel/community/sources?limit=500"),
        api.get("/intel/community/scheduler/status"),
        api.get("/intel/community/kpis"),
      ]);
      setSources(s.data.sources || []);
      setScheduler(sched.data || null);
      setKpis(k.data || null);
    } catch (e) {
      toast.error("Chargement échoué : " + (e?.message || "?"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return sources.filter(s => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (s.name || "").toLowerCase().includes(q)
            || (s.url || "").toLowerCase().includes(q)
            || (s.territory || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [sources, statusFilter, search]);

  const countByStatus = useMemo(() => {
    const c = {};
    sources.forEach(s => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [sources]);

  const runAction = async (sid, action, label) => {
    setBusy(sid + action);
    try {
      const paths = {
        approve:  `/intel/community/sources/${sid}/approve`,
        activate: `/intel/community/sources/${sid}/activate`,
        reject:   `/intel/community/sources/${sid}/reject`,
        deactivate: `/intel/community/sources/${sid}/deactivate`,
        verify:   `/intel/community/sources/${sid}/verify`,
        run:      `/intel/community/sources/${sid}/run`,
        autodetect: `/intel/community/sources/${sid}/auto-detect`,
      };
      const r = await api.post(paths[action]);
      toast.success(label + " ✓");
      if ((action === "verify" || action === "autodetect")
            && selected?.id === sid) {
        setSelected(r.data?.source || null);
      }
      await load();
    } catch (e) {
      toast.error(label + " échoué : " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(""); }
  };

  const runAutoDiscovery = async () => {
    setBusy("auto");
    try {
      const r = await api.post("/intel/community/discover/auto");
      toast.success(`${r.data.inserted} nouvelles sources détectées`);
      await load();
    } catch (e) { toast.error("Découverte échouée"); }
    finally { setBusy(""); }
  };

  const runScheduler = async () => {
    setBusy("tick");
    try {
      const r = await api.post("/intel/community/scheduler/tick");
      toast.success(`Tick : ${r.data.ran} sources traitées`);
      await load();
    } catch (e) { toast.error("Tick échoué"); }
    finally { setBusy(""); }
  };

  const runAutoDetectBatch = async () => {
    setBusy("autodetect-batch");
    try {
      const r = await api.post("/intel/community/sources/auto-detect-pending");
      toast.success(`Auto-detect : ${r.data.processed} testées, `
                       + `${r.data.activated} activées`);
      await load();
    } catch (e) {
      toast.error("Auto-detect en lot échoué : "
                     + (e?.response?.data?.detail || e.message));
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4 p-4" data-testid="sources-manager"
          style={{ background: "#faf9f5", minHeight: "100vh" }}>
      {/* Header + scheduler status */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: NAVY }}
              data-testid="sources-manager-title">
            Sources Manager
          </h1>
          <div className="text-xs" style={{ color: SLATE }}>
            Découverte, vérification, orchestration adaptative
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className="px-2 py-1 rounded flex items-center gap-1"
                style={{ background: scheduler?.running ? "#dcfce7" : "#fecaca",
                          color: scheduler?.running ? "#14532d" : "#991b1b" }}
                data-testid="scheduler-status">
            <Activity size={12} />
            Scheduler {scheduler?.running ? "en cours" : "arrêté"} · dernier tick :
            &nbsp;{fmtDate(scheduler?.last_tick?.at)} · {scheduler?.last_tick?.ran ?? 0} traités
          </div>
          <button onClick={runScheduler} disabled={busy === "tick"}
                    data-testid="btn-force-tick"
                    className="px-2.5 py-1 rounded font-semibold flex items-center gap-1"
                    style={{ background: NAVY, color: "white" }}>
            <Zap size={12} /> Force tick
          </button>
          <button onClick={runAutoDiscovery} disabled={busy === "auto"}
                    data-testid="btn-auto-discovery"
                    className="px-2.5 py-1 rounded font-semibold flex items-center gap-1"
                    style={{ background: CHAMPAGNE, color: NAVY }}>
            <Sparkles size={12} /> Découvrir
          </button>
          <button onClick={runAutoDetectBatch}
                    disabled={busy === "autodetect-batch"}
                    data-testid="btn-autodetect-batch"
                    title="Teste automatiquement RSS/ICS/CKAN/JSON-LD pour chaque source en attente"
                    className="px-2.5 py-1 rounded font-semibold flex items-center gap-1"
                    style={{ background: "#dcfce7", color: "#14532d" }}>
            <Zap size={12} /> Auto-detect en lot
          </button>
          <button onClick={load} data-testid="btn-refresh"
                    className="px-2.5 py-1 rounded flex items-center gap-1"
                    style={{ background: "white", color: NAVY, border: `1px solid ${NAVY}44` }}>
            <RefreshCw size={12} /> Rafraîchir
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="kpi-strip">
          {[
            ["Sources actives", kpis.sources_active, "green"],
            ["Découvertes récentes", kpis.sources_discovered_recent, "blue"],
            ["Dégradées / Bloquées", kpis.sources_degraded, "orange"],
            ["Signaux (30j)", kpis.signals_detected, "navy"],
            ["Discovery Lift", `${Math.round((kpis.discovery_lift_ratio||0)*100)}%`, "gold"],
          ].map(([label, val, tone]) => (
            <div key={label} className="rounded p-3 bg-white"
                  data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: SLATE }}>{label}</div>
              <div className="text-2xl font-bold" style={{ color: NAVY }}>{val ?? "—"}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-2" style={{ color: SLATE }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher…"
                  data-testid="search-sources"
                  className="pl-6 pr-2 py-1 border rounded text-sm"
                  style={{ borderColor: `${NAVY}22`, minWidth: 220 }} />
        </div>
        {["all", "active", "approved", "verifying", "discovered",
            "degraded", "blocked", "rejected", "manual_only"].map(st => (
          <button key={st}
                    data-testid={`filter-${st}`}
                    onClick={() => setStatusFilter(st)}
                    className="px-2 py-1 rounded-full text-[11px] font-semibold"
                    style={{
                      background: statusFilter === st ? NAVY : "white",
                      color: statusFilter === st ? "white" : NAVY,
                      border: `1px solid ${NAVY}33`,
                    }}>
            {st === "all" ? "Toutes" : (STATUS_STYLES[st]?.label || st)}
            {st !== "all" && countByStatus[st] ? ` · ${countByStatus[st]}` : ""}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded border overflow-x-auto"
            style={{ borderColor: `${NAVY}22` }}>
        <table className="w-full text-xs" data-testid="sources-table">
          <thead>
            <tr style={{ background: "#f8fafc", color: SLATE }}>
              <th className="text-left p-2">Nom / Territoire</th>
              <th className="text-left p-2">Type / Méthode</th>
              <th className="text-left p-2">Statut</th>
              <th className="text-left p-2">Confiance</th>
              <th className="text-left p-2">Signaux</th>
              <th className="text-left p-2">Cadence</th>
              <th className="text-left p-2">Dernier essai</th>
              <th className="text-left p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="p-4 text-center" style={{ color: SLATE }}>
                Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="p-4 text-center" style={{ color: SLATE }}>
                Aucune source ne correspond aux filtres.</td></tr>
            )}
            {filtered.map(s => (
              <tr key={s.id} className="border-t hover:bg-slate-50"
                    style={{ borderColor: `${NAVY}11` }}
                    data-testid={`source-row-${s.id}`}>
                <td className="p-2">
                  <div className="font-semibold" style={{ color: NAVY }}>{s.name}</div>
                  <div className="text-[10px]" style={{ color: SLATE }}>
                    {s.territory || "—"}
                    {s.url && <> · <a href={s.url} target="_blank" rel="noopener noreferrer"
                      className="underline">source</a></>}
                  </div>
                </td>
                <td className="p-2">
                  <div style={{ color: NAVY }}>{s.type}</div>
                  <div className="text-[10px]" style={{ color: SLATE }}>{s.access_method}</div>
                </td>
                <td className="p-2"><StatusPill status={s.status} /></td>
                <td className="p-2" style={{ color: NAVY }}>
                  {s.confidence_level != null
                    ? `${Math.round(s.confidence_level * 100)}%` : "—"}
                </td>
                <td className="p-2" style={{ color: NAVY }}>
                  <div>{s.signals_detected || 0}
                    {s.new_signals_last_run > 0 &&
                      <span style={{ color: "#15803d" }}> · +{s.new_signals_last_run}</span>}
                  </div>
                  {s.errors_count > 0 && (
                    <div className="text-[10px]" style={{ color: "#991b1b" }}>
                      {s.errors_count} erreur(s)
                    </div>
                  )}
                </td>
                <td className="p-2 text-[10px]" style={{ color: NAVY }}>
                  {s.check_frequency_hours}h
                </td>
                <td className="p-2 text-[10px]" style={{ color: SLATE }}>
                  <div>{fmtDate(s.last_attempt_at)}</div>
                  {s.last_error && <div style={{ color: "#991b1b" }}
                                          title={s.last_error}>{s.last_error.slice(0, 40)}…</div>}
                </td>
                <td className="p-2 whitespace-nowrap">
                  <div className="flex gap-1">
                    {(s.status === "discovered" || s.status === "verifying" ||
                        s.status === "candidate") && (
                      <button onClick={() => runAction(s.id, "approve", "Approuver")}
                                title="Approuver"
                                data-testid={`btn-approve-${s.id}`}
                                className="p-1 rounded" style={{ background: "#dcfce7" }}>
                        <Check size={12} color="#15803d" />
                      </button>
                    )}
                    {s.status === "approved" && (
                      <button onClick={() => runAction(s.id, "activate", "Activer")}
                                title="Activer"
                                data-testid={`btn-activate-${s.id}`}
                                className="p-1 rounded" style={{ background: "#bbf7d0" }}>
                        <PlayCircle size={12} color="#14532d" />
                      </button>
                    )}
                    {(s.status === "active" || s.status === "degraded") && (
                      <button onClick={() => runAction(s.id, "deactivate", "Désactiver")}
                                title="Désactiver"
                                data-testid={`btn-deactivate-${s.id}`}
                                className="p-1 rounded" style={{ background: "#fee2e2" }}>
                        <PauseCircle size={12} color="#991b1b" />
                      </button>
                    )}
                    {s.status !== "rejected" && (
                      <button onClick={() => runAction(s.id, "reject", "Rejeter")}
                                title="Rejeter"
                                data-testid={`btn-reject-${s.id}`}
                                className="p-1 rounded" style={{ background: "#fecaca" }}>
                        <Ban size={12} color="#991b1b" />
                      </button>
                    )}
                    <button onClick={() => runAction(s.id, "verify", "Vérifier")}
                              title="Vérifier"
                              data-testid={`btn-verify-${s.id}`}
                              className="p-1 rounded" style={{ background: "#fef3c7" }}>
                      <AlertCircle size={12} color="#92400e" />
                    </button>
                    <button onClick={() => runAction(s.id, "autodetect",
                                                          "Auto-détection")}
                              title="Auto-détection (RSS/ICS/CKAN/JSON-LD/Eventbrite)"
                              data-testid={`btn-autodetect-${s.id}`}
                              className="p-1 rounded"
                              style={{ background: "#dcfce7" }}>
                      <Zap size={12} color="#15803d" />
                    </button>
                    <button onClick={() => setSelected(s)}
                              title="Diagnostic"
                              data-testid={`btn-diagnose-${s.id}`}
                              className="p-1 rounded" style={{ background: "#e0f2fe" }}>
                      <Activity size={12} color="#0369a1" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Diagnostic drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40"
              onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}
              data-testid="diagnostic-panel">
          <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold" style={{ color: NAVY }}>{selected.name}</h3>
                <div className="text-[11px]" style={{ color: SLATE }}>{selected.url}</div>
              </div>
              <button onClick={() => setSelected(null)}
                        data-testid="close-diagnostic"
                        style={{ color: SLATE }}>
                <X size={16} />
              </button>
            </div>
            <div className="space-y-2 text-xs">
              <div><b>Statut :</b> <StatusPill status={selected.status} /></div>
              <div><b>Type :</b> {selected.type} · <b>Méthode :</b> {selected.access_method}</div>
              <div><b>Territoire :</b> {selected.territory || "—"}</div>
              <div><b>Découverte depuis :</b> {selected.discovered_from || "—"}</div>
              <div><b>Robots.txt :</b> {selected.robots_allowed == null ? "?" :
                    selected.robots_allowed ? "autorisé" : "interdit"}</div>
              <div><b>Content-Type :</b> {selected.content_type_seen || "—"}</div>
              <div><b>Événements futurs :</b> {selected.has_future_events == null ? "?" :
                    selected.has_future_events ? "oui" : "non"}</div>
              <div><b>Licence :</b> {selected.license_hint || "—"}</div>
              <div><b>Confiance système :</b> {Math.round((selected.confidence_level||0)*100)}%</div>
              <div><b>Qualité :</b> {Math.round((selected.quality_score||0)*100)}%</div>
              <div><b>Cadence :</b> {selected.check_frequency_hours}h ·
                &nbsp;prochain passage : {fmtDate(selected.next_check_at)}</div>
              <div><b>Signaux :</b> {selected.signals_detected || 0} détectés ·
                {selected.signals_relevant || 0} pertinents ·
                {selected.new_signals_last_run || 0} nouveaux au dernier run</div>
              <div><b>Erreurs :</b> {selected.errors_count || 0}
                {selected.consecutive_errors > 0 &&
                  ` (${selected.consecutive_errors} consécutives)`}</div>
              {selected.last_error && (
                <div className="rounded p-2 text-[10px]"
                      style={{ background: "#fee2e2", color: "#991b1b" }}>
                  <b>Dernière erreur :</b> {selected.last_error}
                </div>
              )}
              {selected.verification_report?.steps?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer font-semibold" style={{ color: NAVY }}>
                    Rapport de vérification
                  </summary>
                  <pre className="mt-2 p-2 rounded text-[10px] overflow-x-auto"
                        style={{ background: "#f8fafc" }}>
                    {JSON.stringify(selected.verification_report, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
